import React from "react";

/**
 * 1200x628 share card for the public Track Record / Performance Dashboard.
 *
 * Props (all numbers, optional):
 *   periodLabel        e.g. "Last 1 year"
 *   closedSignals
 *   winRate            e.g. 54.9
 *   profitFactor       e.g. 2.11
 *   scannerReturn      e.g. 443.17    (% scanner cumulative)
 *   niftyReturn        e.g. -3.42     (% NIFTY 50 in same window)
 *   avgWin             e.g. 21.43
 *   avgLoss            e.g. -12.38
 */
const sign = (n) => (n > 0 ? "+" : n < 0 ? "−" : "");
const abs = (n) =>
  typeof n === "number" && Number.isFinite(n)
    ? Math.abs(n).toFixed(n > 100 || n < -100 ? 0 : 2)
    : "—";

export default function ShareTrackRecordCard({
  periodLabel = "Live Track Record",
  closedSignals = 0,
  winRate = 0,
  profitFactor = 0,
  scannerReturn = 0,
  niftyReturn = 0,
  avgWin = 0,
  avgLoss = 0,
}) {
  const beatsNifty = scannerReturn - niftyReturn;
  return (
    <div
      style={{
        width: 1200, height: 628,
        background: "linear-gradient(135deg, #0a0e0c 0%, #061412 100%)",
        color: "#e7f7ee",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        position: "relative", overflow: "hidden",
        padding: "48px 56px",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}
      data-testid="share-track-record-card"
    >
      {/* Grid bg */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage:
          "linear-gradient(rgba(40,255,180,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(40,255,180,0.05) 1px, transparent 1px)",
        backgroundSize: "44px 44px", opacity: 0.5,
      }} />
      <div style={{
        position: "absolute", bottom: -200, left: -200, width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(40,255,180,0.15), transparent 70%)", filter: "blur(60px)",
      }} />

      {/* Header */}
      <div style={{ position: "relative" }}>
        <div style={{
          fontSize: 14, letterSpacing: "0.3em", textTransform: "uppercase",
          color: "#28ffb4", fontWeight: 700, marginBottom: 12,
        }}>
          /// {periodLabel}
        </div>
        <div style={{ fontSize: 56, fontWeight: 600, letterSpacing: "-0.02em", color: "#fff", lineHeight: 1.05 }}>
          Real Performance · Not Backtests
        </div>
      </div>

      {/* Hero stat — scanner vs Nifty */}
      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 28 }}>
        <div style={{
          padding: "26px 30px", border: "1px solid rgba(40,255,180,0.35)",
          background: "rgba(40,255,180,0.06)", borderRadius: 4,
        }}>
          <div style={{ fontSize: 13, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 12 }}>
            Scanner cumulative return
          </div>
          <div style={{
            fontSize: 96, fontWeight: 800, lineHeight: 1,
            color: scannerReturn >= 0 ? "#28ffb4" : "#ff6464",
          }}>
            {sign(scannerReturn)}{abs(scannerReturn)}%
          </div>
          <div style={{ marginTop: 12, fontSize: 16, color: "#7a8a82" }}>
            NIFTY 50 ·{" "}
            <span style={{ color: niftyReturn >= 0 ? "#28ffb4" : "#ff6464", fontWeight: 700 }}>
              {sign(niftyReturn)}{abs(niftyReturn)}%
            </span>
            {" "}· Outperformance{" "}
            <span style={{
              color: beatsNifty >= 0 ? "#28ffb4" : "#ff6464",
              fontWeight: 700,
            }}>
              {sign(beatsNifty)}{abs(beatsNifty)}%
            </span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 14 }}>
          <div style={{
            padding: "16px 22px", border: "1px solid rgba(140,160,150,0.2)",
            background: "rgba(20,30,26,0.6)", borderRadius: 4,
          }}>
            <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 6 }}>
              Win Rate
            </div>
            <div style={{ fontSize: 38, fontWeight: 700, color: "#fff" }}>
              {winRate?.toFixed?.(1) ?? "—"}<span style={{ fontSize: 22, color: "#7a8a82" }}>%</span>
            </div>
          </div>
          <div style={{
            padding: "16px 22px", border: "1px solid rgba(140,160,150,0.2)",
            background: "rgba(20,30,26,0.6)", borderRadius: 4,
          }}>
            <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 6 }}>
              Profit Factor
            </div>
            <div style={{
              fontSize: 38, fontWeight: 700,
              color: profitFactor >= 2 ? "#28ffb4" : "#fff",
            }}>
              {profitFactor?.toFixed?.(2) ?? "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        {[
          { label: "Closed Signals", value: closedSignals, color: "#fff" },
          { label: "Avg Win", value: `${sign(avgWin)}${abs(avgWin)}%`, color: "#28ffb4" },
          { label: "Avg Loss", value: `${sign(avgLoss)}${abs(avgLoss)}%`, color: "#ff6464" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            padding: "14px 18px", border: "1px solid rgba(140,160,150,0.18)",
            background: "rgba(20,30,26,0.5)", borderRadius: 4,
          }}>
            <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7a8a82", marginBottom: 6 }}>
              {label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, color: "#7a8a82", maxWidth: 700 }}>
          Live closed signals from our public dashboard. Past performance does not guarantee future returns.
        </div>
        <div style={{
          color: "#28ffb4", fontSize: 16, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
        }}>
          ▲ breakoutscanner.co.in
        </div>
      </div>
    </div>
  );
}
